import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FiPlus, FiEdit2, FiEdit3, FiTrash2, FiEye, FiSend, FiPause,
  FiSearch, FiRefreshCw, FiActivity,
} from 'react-icons/fi';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInGate from '../../components/GoogleSignInGate';
import UserBadge from '../../components/UserBadge';
import ConfirmModal from '../../components/ConfirmModal';
import { apiGetAllPosts, apiUpdatePost, apiDeletePost, apiResetBlog } from '../../api/blogApi';

export default function BlogAdmin() {
  useAuth();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => { refresh(); }, []);

  const refresh = () => {
    apiGetAllPosts().then((res) => {
      if (res.ok) setPosts(res.data);
    });
  };

  const filtered = posts
    .filter((p) => {
      if (filter === 'published') return p.status === 'published';
      if (filter === 'draft') return p.status === 'draft';
      if (filter === 'featured') return p.featured && p.status === 'published';
      return true;
    })
    .filter((p) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return p.title.toLowerCase().includes(q) || (p.author || '').toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (a.status === 'published' && b.status === 'published') {
        return new Date(b.publishedDate) - new Date(a.publishedDate);
      }
      return a.status === 'draft' ? -1 : 1;
    });

  const stats = {
    total: posts.length,
    published: posts.filter((p) => p.status === 'published').length,
    drafts: posts.filter((p) => p.status === 'draft').length,
    featured: posts.filter((p) => p.featured).length,
  };

  /* ── Mini chart data ── */
  const blogCategoryData = useMemo(() => {
    const map = {};
    posts.forEach((p) => {
      const cat = p.category || 'Other';
      map[cat] = (map[cat] || 0) + 1;
    });
    return Object.entries(map).map(([name, count]) => ({ name, count }));
  }, [posts]);

  const blogTimelineData = useMemo(() => {
    const months = {};
    posts.filter((p) => p.publishedDate).forEach((p) => {
      const d = new Date(p.publishedDate);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('en-US', { month: 'short' });
      if (!months[key]) months[key] = { key, name: label, articles: 0 };
      months[key].articles++;
    });
    return Object.values(months).sort((a, b) => a.key.localeCompare(b.key));
  }, [posts]);

  const blogStatusPie = useMemo(() => {
    return [
      { name: 'Published', value: stats.published, color: '#059669' },
      { name: 'Drafts', value: stats.drafts, color: '#f59e0b' },
    ].filter((d) => d.value > 0);
  }, [stats.published, stats.drafts]);

  const COLORS = ['#0052cc', '#7c3aed', '#059669', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];

  const handlePublish = (id) => {
    apiUpdatePost(id, { status: 'published', publishedDate: new Date().toISOString().split('T')[0] }).then(() => refresh());
  };

  const handleUnpublish = (id) => {
    apiUpdatePost(id, { status: 'draft' }).then(() => refresh());
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this article permanently?')) {
      apiDeletePost(id).then(() => refresh());
    }
  };

  const handleToggleFeatured = (id, current) => {
    apiUpdatePost(id, { featured: !current }).then(() => refresh());
  };

  const handleReset = async () => {
    setResetLoading(true);
    try {
      await apiResetBlog();
      await refresh();
    } catch (err) {
      console.error('Reset failed:', err);
    }
    setResetLoading(false);
    setShowResetModal(false);
  };

  return (
    <GoogleSignInGate>
      <div className="admin-page">
        {/* Shared admin header */}
        <div className="admin-header">
          <div className="admin-header-inner">
            <Link to="/" className="research-brand">
              <span className="research-brand-text">
                <strong>Vector Labs</strong> Admin
              </span>
            </Link>
            <nav className="admin-nav">
              <Link to="/admin/writing" className="admin-nav-link"><FiEdit3 style={{marginRight:4}} />Writing</Link>
              <Link to="/admin/usage" className="admin-nav-link"><FiActivity style={{marginRight:4}} />Usage</Link>
              <Link to="/" className="admin-nav-link">Home</Link>
            </nav>
            <UserBadge />
          </div>
        </div>

        <div className="admin-body">
          {/* Stats bar */}
          <div className="admin-stats">
            <div className="stat-card">
              <span className="stat-number">{stats.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{stats.published}</span>
              <span className="stat-label">Published</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{stats.drafts}</span>
              <span className="stat-label">Drafts</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{stats.featured}</span>
              <span className="stat-label">Featured</span>
            </div>
          </div>

          {/* Mini charts row */}
          <div className="admin-mini-charts">
            <div className="mini-chart-card">
              <h4>Posts by Category</h4>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={blogCategoryData} margin={{ left: -10, right: 5, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" fontSize={9} stroke="#94a3b8" tick={{ fill: 'rgba(255,255,255,0.45)' }} angle={-20} textAnchor="end" height={50} />
                  <YAxis fontSize={10} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Articles" radius={[4, 4, 0, 0]} animationDuration={1000}>
                    {blogCategoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mini-chart-card">
              <h4>Publishing Timeline</h4>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={blogTimelineData} margin={{ left: -10, right: 10, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" fontSize={10} stroke="#94a3b8" tick={{ fill: 'rgba(255,255,255,0.45)' }} />
                  <YAxis fontSize={10} stroke="#94a3b8" allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="articles" name="Articles" stroke="#7c3aed" strokeWidth={2.5}
                    dot={{ r: 4, fill: '#7c3aed' }} activeDot={{ r: 6 }} animationDuration={1500} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mini-chart-card">
              <h4>Status Breakdown</h4>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={blogStatusPie} cx="50%" cy="50%" innerRadius={35} outerRadius={65}
                    paddingAngle={4} dataKey="value" animationDuration={1000}
                    label={({ name, value }) => `${name} (${value})`} >
                    {blogStatusPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Toolbar */}
          <div className="admin-toolbar">
            <div className="admin-filters">
              {['all', 'published', 'draft', 'featured'].map((f) => (
                <button
                  key={f}
                  className={`admin-filter-btn ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)} ({
                    f === 'all' ? stats.total :
                    f === 'published' ? stats.published :
                    f === 'draft' ? stats.drafts :
                    stats.featured
                  })
                </button>
              ))}
            </div>
            <div className="admin-actions-bar">
              <div className="admin-search-inline">
                <FiSearch />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search articles..."
                />
              </div>
              <button className="admin-btn secondary" onClick={() => setShowResetModal(true)} title="Reset blog data">
                <FiRefreshCw /> Reset Data
              </button>
              <Link to="/admin/blog/new" className="admin-btn primary">
                <FiPlus /> New Article
              </Link>
            </div>
          </div>

          {/* Table */}
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Author</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((post) => (
                  <tr key={post.id}>
                    <td className="admin-title-cell">
                      <Link to={post.status === 'published' ? `/blog/${post.slug}` : `/admin/blog/edit/${post.id}`}>
                        {post.title}
                      </Link>
                      {post.featured && <span className="admin-cat-pill" style={{ marginLeft: 8, background: '#d97706', color: '#fff' }}>★ Featured</span>}
                    </td>
                    <td><span className="admin-cat-pill">{post.category}</span></td>
                    <td className="admin-authors-cell">{post.author}</td>
                    <td>
                      <span className={`admin-status ${post.status}`}>
                        {post.status}
                      </span>
                    </td>
                    <td className="admin-date-cell">
                      {post.publishedDate ? new Date(post.publishedDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="admin-actions-cell">
                      {post.status === 'published' && (
                        <Link to={`/blog/${post.slug}`} className="admin-action-btn edit" title="View">
                          <FiEye />
                        </Link>
                      )}
                      <Link to={`/admin/blog/edit/${post.id}`} className="admin-action-btn edit" title="Edit">
                        <FiEdit2 />
                      </Link>
                      {post.status === 'draft' ? (
                        <button className="admin-action-btn publish" onClick={() => handlePublish(post.id)} title="Publish">
                          <FiSend />
                        </button>
                      ) : (
                        <button className="admin-action-btn unpublish" onClick={() => handleUnpublish(post.id)} title="Unpublish">
                          <FiPause />
                        </button>
                      )}
                      <button
                        className={`admin-action-btn ${post.featured ? 'publish' : ''}`}
                        onClick={() => handleToggleFeatured(post.id, post.featured)}
                        title={post.featured ? 'Unfeature' : 'Feature'}
                      >
                        ★
                      </button>
                      <button className="admin-action-btn delete" onClick={() => handleDelete(post.id)} title="Delete">
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No articles found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ConfirmModal
          open={showResetModal}
          title="Reset Blog Data"
          message={"This will permanently delete all blog articles and reset to an empty state.\nThis action cannot be undone."}
          confirmLabel="Reset All Data"
          variant="danger"
          onConfirm={handleReset}
          onCancel={() => setShowResetModal(false)}
          loading={resetLoading}
        />
      </div>
    </GoogleSignInGate>
  );
}
